import { IconRemove, Switch } from '@newfront-insurance/core-ui';
import { Download, Loader2, Pencil, Share2 } from 'lucide-react';

import {
  getCustomSkillDisplayText,
  type CustomSkillManagementTab,
  type PendingSkillAction,
} from './custom-skill-model';
import type { CustomSkillRecord, NovaSkill } from '../../../../queries/chats';
import { SettingsActionButton } from '../settings-shared/settings-action-button';
import {
  SkillListChevron,
  SkillListExampleUsage,
  SkillListExpandedHint,
  SkillListExpandedSection,
  SkillListItem,
} from '../skill-list-item';

export function CompactCustomSkillRow({
  skill,
  listScope,
  runtimeSkill,
  pendingAction,
  isDownloading,
  isExpanded,
  showSharingControls,
  onToggle,
  onToggleExpanded,
  onDownload,
  onEdit,
  onShare,
  onDelete,
}: {
  skill: CustomSkillRecord;
  listScope: CustomSkillManagementTab;
  runtimeSkill?: NovaSkill;
  pendingAction: PendingSkillAction | null;
  isDownloading: boolean;
  isExpanded: boolean;
  showSharingControls: boolean;
  onToggle: () => void;
  onToggleExpanded: () => void;
  onDownload: () => void;
  onEdit: () => void;
  onShare: () => void;
  onDelete: () => void;
}) {
  const isPending = pendingAction?.uuid === skill.uuid || isDownloading;
  const isSharedList = listScope === 'shared-with-me';

  return (
    <SkillListItem
      handle={`/${skill.command}`}
      description={getCustomSkillDisplayText(skill.frontmatter.summary, skill.frontmatter.description)}
      variant="compact"
      isExpanded={isExpanded}
      isDisabled={pendingAction?.type === 'delete' && pendingAction.uuid === skill.uuid}
      onPrimaryAction={onToggleExpanded}
      primaryActionAriaLabel={
        isExpanded ? `Collapse details for ${skill.frontmatter.name}` : `Open details for ${skill.frontmatter.name}`
      }
      inlineActions={
        <>
          {isSharedList ? (
            <div className="flex items-center gap-2">
              <span className="hidden text-[12px] leading-4 text-[#546A83] sm:inline">
                {skill.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch enabled={skill.enabled} onClick={onToggle} locked={isPending} />
            </div>
          ) : (
            <>
              {skill.sourceFileUuid ? (
                <SettingsActionButton
                  label={`Download ${skill.frontmatter.name}`}
                  disabled={isPending}
                  onClick={onDownload}
                  testId="download-skill-button"
                  ariaBusy={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-[#546A83]" />
                  ) : (
                    <Download aria-hidden="true" className="h-4 w-4 text-[#546A83]" />
                  )}
                </SettingsActionButton>
              ) : null}

              <SettingsActionButton label={`Edit ${skill.frontmatter.name}`} disabled={isPending} onClick={onEdit}>
                <Pencil aria-hidden="true" className="h-4 w-4" />
              </SettingsActionButton>

              {showSharingControls ? (
                <SettingsActionButton label={`Share ${skill.frontmatter.name}`} disabled={isPending} onClick={onShare}>
                  <Share2 aria-hidden="true" className="h-4 w-4" />
                </SettingsActionButton>
              ) : null}

              <SettingsActionButton label={`Delete ${skill.frontmatter.name}`} disabled={isPending} onClick={onDelete}>
                <span aria-hidden="true">
                  <IconRemove color="currentColor" size={16} />
                </span>
              </SettingsActionButton>
            </>
          )}
        </>
      }
      trailingAction={
        <SkillListChevron
          isExpanded={isExpanded}
          onClick={onToggleExpanded}
          label={isExpanded ? `Collapse ${skill.frontmatter.name}` : `Expand ${skill.frontmatter.name}`}
          disabled={isPending}
          variant="compact"
        />
      }
      expandedContent={
        <>
          {!isSharedList ? (
            <div className="flex items-center gap-2 px-1 py-0.5">
              <span className="text-[12px] leading-4 text-[#546A83]">
                {skill.enabled ? 'Disable skill' : 'Enable skill'}
              </span>
              <Switch enabled={skill.enabled} onClick={onToggle} locked={isPending} />
            </div>
          ) : null}

          <SkillListExpandedSection label="What it does" variant="compact">
            <SkillListExpandedHint variant="compact">
              {getCustomSkillDisplayText(skill.frontmatter.overview, skill.frontmatter.description)}
            </SkillListExpandedHint>
          </SkillListExpandedSection>

          {runtimeSkill?.exampleUsage ? (
            <SkillListExpandedSection label="Example usage" variant="compact">
              <SkillListExampleUsage handle={skill.command} variant="compact">
                {runtimeSkill.exampleUsage}
              </SkillListExampleUsage>
            </SkillListExpandedSection>
          ) : null}
        </>
      }
    />
  );
}
